/**
 * Connection requests on the Connections page: BOX "received" — people asking to connect with you (accept: they
 * see your whole profile), "sent" — your requests waiting for an answer (withdraw).
 * Backend: GET /interact/follow-requests/?box=received|sent, POST …/<id>/accept|decline|withdraw/.
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { Alert, Avatar, Box, Button, CircularProgress, Typography } from '@mui/material';
import { fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { ACCEPT_BUTTON, DECLINE_BUTTON, answerRequest, requestsUrl } from './connectionsApi';

const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function FollowRequestList({ box = 'received', search = '' }) {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { data, error } = useSWR(requestsUrl(box), fetcher);
  const [busy, setBusy] = useState(null);

  const run = async (id, verb, done) => {
    setBusy(id);
    try {
      await answerRequest(id, verb);
      showSnackbar(done, 'success');
    } catch (err) {
      showSnackbar(apiErrorMessage(err, 'That did not work. Please try again.'), 'error');
    } finally {
      setBusy(null);
    }
  };

  if (error) return <Alert severity="error">Your connection requests could not be loaded.</Alert>;
  if (!data) return <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} /></Box>;
  const words = search.trim().toLowerCase();
  const rows = data.filter((r) => !words || [r.user.name, r.user.position].some((v) => (v || '').toLowerCase().includes(words)));
  if (rows.length === 0) {
    return <Alert severity="info">{box === 'sent' ? 'No requests waiting for an answer.' : 'No requests waiting for your answer.'}</Alert>;
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
      {rows.map((r) => (
        <Box key={r.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', px: 2.5, py: 1.5,
          borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
          <Avatar src={r.user.avatar_url || undefined} onClick={() => navigate(`/profile/${r.user.id}/`)}
            sx={{ width: 42, height: 42, cursor: 'pointer', bgcolor: 'primary.avatar', color: '#fff', fontWeight: 600 }}>
            {(r.user.name || '?')[0]}
          </Avatar>
          <Box flex={1} minWidth={0} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/profile/${r.user.id}/`)}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }} noWrap>{r.user.name}</Typography>
            {r.user.position && <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>{r.user.position}</Typography>}
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
              {box === 'sent' ? `Asked ${day(r.created)}` : `Wants to connect with you · ${day(r.created)}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {box === 'sent' ? (
              <Button {...DECLINE_BUTTON} disabled={busy === r.id} onClick={() => run(r.id, 'withdraw', 'Request withdrawn')}>
                Withdraw
              </Button>
            ) : (
              <>
                <Button {...ACCEPT_BUTTON} disabled={busy === r.id}
                  onClick={() => run(r.id, 'accept', `You are now connected with ${r.user.name.split(' ')[0]}`)}>Accept</Button>
                <Button {...DECLINE_BUTTON} disabled={busy === r.id}
                  onClick={() => run(r.id, 'decline', 'Request declined')}>Decline</Button>
              </>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
